<template>
  <div class="inventory-page">
    <header class="hero">
      <div class="hero-copy">
        <p class="eyebrow">Fulfillrite</p>
        <h1>Inventory coverage</h1>
        <p class="lede">
          Months left is current available stock divided by the average units sold per month.
          Sales come from Fulfillrite orders.
        </p>
      </div>
    </header>

    <div class="controls">
      <div class="scope-tabs" role="tablist" aria-label="Sales window">
        <button
          type="button"
          class="scope-tab"
          :class="{ active: months === 12 }"
          @click="setMonths(12)"
        >
          Last year
        </button>
        <button
          type="button"
          class="scope-tab"
          :class="{ active: months === 6 }"
          @click="setMonths(6)"
        >
          Last 6 months
        </button>
      </div>

      <div class="scope-tabs" role="tablist" aria-label="Rank">
        <button
          type="button"
          class="scope-tab"
          :class="{ active: !sortKey && rank === 'fewest' }"
          @click="setRank('fewest')"
        >
          Fewest months first
        </button>
        <button
          type="button"
          class="scope-tab"
          :class="{ active: !sortKey && rank === 'most' }"
          @click="setRank('most')"
        >
          Most months first
        </button>
      </div>

      <label class="check">
        <input type="checkbox" v-model="ignoreDyno" @change="load" />
        Ignore sample orders (DYNO, JQ-, SP-)
      </label>

      <label class="check">
        <input type="checkbox" v-model="activeOnly" @change="load" />
        Active products only
      </label>

      <input
        v-model.trim="query"
        type="search"
        class="search"
        placeholder="Filter by SKU or description"
      />
    </div>

    <p v-if="loading" class="loading-message">Loading inventory…</p>
    <p v-else-if="error" class="error-message">{{ error }}</p>
    <template v-else>
      <p class="count-msg">
        {{ visibleRows.length }} products
        <span v-if="sinceLabel"> · sales since {{ sinceLabel }}</span>
      </p>
      <div class="scroll-box">
        <table class="table">
          <thead>
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                :class="[col.numeric ? 'num' : '', 'sortable', { sorted: sortKey === col.key }]"
                @click="cycleSort(col.key)"
              >
                {{ col.label }}
                <span class="sort-mark" aria-hidden="true">{{ sortMark(col.key) }}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in visibleRows"
              :key="row.sku"
              :class="{ urgent: isUrgent(row) }"
            >
              <td>{{ row.sku }}</td>
              <td>{{ row.description || '—' }}</td>
              <td class="num">{{ formatCount(row.available) }}</td>
              <td class="num">{{ formatCount(row.on_hand) }}</td>
              <td class="num">{{ formatCount(row.units_sold) }}</td>
              <td class="num">{{ formatCount(row.avg_per_month) }}</td>
              <td class="num months">{{ formatMonths(row.months_left) }}</td>
            </tr>
            <tr v-if="!visibleRows.length">
              <td colspan="7" class="empty">No products match.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import InventoryService from '@/services/InventoryService'

export default {
  name: 'InventoryPage',
  data() {
    return {
      months: 12,
      rank: 'fewest',
      ignoreDyno: false,
      activeOnly: true,
      query: '',
      sortKey: null,
      sortDir: 'asc',
      columns: [
        { key: 'sku', label: 'SKU' },
        { key: 'description', label: 'Description' },
        { key: 'available', label: 'Available', numeric: true },
        { key: 'on_hand', label: 'On hand', numeric: true },
        { key: 'units_sold', label: 'Units sold', numeric: true },
        { key: 'avg_per_month', label: 'Avg / month', numeric: true },
        { key: 'months_left', label: 'Months left', numeric: true }
      ],
      rows: [],
      since: null,
      loading: false,
      error: ''
    }
  },
  computed: {
    sinceLabel() {
      if (!this.since) return ''
      const date = new Date(this.since)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },
    visibleRows() {
      const q = this.query.toLowerCase()
      const filtered = q
        ? this.rows.filter(row =>
          `${row.sku} ${row.description || ''}`.toLowerCase().includes(q)
        )
        : this.rows.slice()
      const direction = this.sortKey
        ? (this.sortDir === 'desc' ? -1 : 1)
        : (this.rank === 'most' ? -1 : 1)
      const key = this.sortKey || 'months_left'
      const numeric = key !== 'sku' && key !== 'description'
      filtered.sort((a, b) => {
        const cmp = numeric
          ? this.compareNumber(a[key], b[key], direction)
          : String(a[key] || '').localeCompare(String(b[key] || ''), undefined, { sensitivity: 'base' }) * direction
        if (cmp !== 0) return cmp
        return String(a.sku).localeCompare(String(b.sku))
      })
      return filtered
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    setMonths(months) {
      if (this.months === months) return
      this.months = months
      this.load()
    },
    setRank(rank) {
      this.rank = rank
      this.sortKey = null
    },
    cycleSort(key) {
      if (this.sortKey !== key) {
        this.sortKey = key
        this.sortDir = 'asc'
        return
      }
      if (this.sortDir === 'asc') {
        this.sortDir = 'desc'
        return
      }
      this.sortKey = null
      this.sortDir = 'asc'
    },
    sortMark(key) {
      if (this.sortKey !== key) return ''
      return this.sortDir === 'asc' ? '▲' : '▼'
    },
    compareNumber(left, right, direction) {
      const a = left == null || left === '' ? null : Number(left)
      const b = right == null || right === '' ? null : Number(right)
      const aMissing = a == null || !Number.isFinite(a)
      const bMissing = b == null || !Number.isFinite(b)
      if (aMissing && bMissing) return 0
      if (aMissing) return 1
      if (bMissing) return -1
      return (a - b) * direction
    },
    formatCount(value) {
      if (value == null || value === '') return '—'
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return Number.isInteger(n) ? String(n) : n.toFixed(1)
    },
    isUrgent(row) {
      const n = Number(row && row.months_left)
      return Number.isFinite(n) && n < 6
    },
    formatMonths(value) {
      if (value == null) return '—'
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(n >= 10 ? 1 : 2)
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const { data } = await InventoryService.coverage({
          months: this.months,
          ignoreDyno: this.ignoreDyno,
          productFilter: this.activeOnly ? 'active' : 'all'
        })
        this.rows = Array.isArray(data.products) ? data.products : []
        this.since = data.since || null
      } catch (err) {
        this.rows = []
        this.error = 'Could not load inventory coverage.'
        console.error(err)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.inventory-page {
  padding: 1.25rem 1.5rem 2rem;
  color: #fff;
}
.eyebrow {
  margin: 0 0 0.2rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 0.75rem;
  color: #1abc9c;
}
h1 {
  margin: 0 0 0.35rem;
  font-size: 1.8rem;
}
.lede {
  margin: 0;
  max-width: 46rem;
  color: rgba(255, 255, 255, 0.78);
  line-height: 1.45;
}
.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  align-items: center;
  margin: 1.1rem 0 0.8rem;
}
.scope-tabs {
  display: inline-flex;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.25);
}
.scope-tab {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  padding: 0.4rem 0.95rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
}
.scope-tab.active {
  background: rgba(26, 188, 156, 0.22);
  color: #a7f3d0;
}
.check {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.88);
}
.search {
  min-width: 16rem;
  padding: 0.45rem 0.7rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(0, 0, 0, 0.25);
  color: #fff;
}
.loading-message, .error-message {
  font-style: italic;
  color: #ffe08a;
}
.count-msg {
  margin: 0 0 0.55rem;
  color: rgba(255, 255, 255, 0.82);
}
.scroll-box {
  max-height: 70vh;
  overflow: auto;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
}
.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
}
th, td {
  padding: 0.55rem 0.7rem;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
th {
  position: sticky;
  top: 0;
  background: #1b2430;
  color: rgba(255, 255, 255, 0.72);
  font-weight: 600;
}
th.sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}
th.sortable:hover,
th.sorted {
  color: #a7f3d0;
}
.sort-mark {
  display: inline-block;
  min-width: 0.9em;
  margin-left: 0.2rem;
  font-size: 0.7rem;
}
.num { text-align: right; font-variant-numeric: tabular-nums; }
.months { font-weight: 650; }
@keyframes magma {
  0%, 100% {
    background-color: rgba(92, 12, 8, 0.55);
    box-shadow: inset 3px 0 0 #b91c1c;
  }
  45% {
    background-color: rgba(185, 28, 28, 0.72);
    box-shadow: inset 3px 0 0 #ff5a1f, inset 0 0 18px rgba(255, 80, 20, 0.35);
  }
  70% {
    background-color: rgba(234, 88, 12, 0.55);
    box-shadow: inset 3px 0 0 #fb7185, inset 0 0 22px rgba(255, 40, 20, 0.45);
  }
}
tr.urgent td {
  color: #ffe8dc;
  animation: magma 1.7s ease-in-out infinite;
}
tr.urgent .months {
  color: #ffb4a2;
  font-weight: 800;
  text-shadow: 0 0 10px rgba(255, 70, 20, 0.9);
}
@media (prefers-reduced-motion: reduce) {
  tr.urgent td {
    animation: none;
    background-color: rgba(153, 27, 27, 0.62);
    box-shadow: inset 3px 0 0 #ef4444;
  }
}
.empty {
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}
</style>
